import { SpaDay } from '@routes/SpaDay';
import * as yourJourneyActions from '@store/yourJourney/actions';
import moment from 'moment-timezone';
import NextSeo from 'next-seo';
import Error from 'next/error';
import Head from 'next/head';
import { useEffect } from 'react';

import { getSpaPackagePrefetch, useGetSpaPackage } from '@common/features/spa/hooks';
import { analytics } from '@features/analytics';
import { settingSelectors } from '@store/setting/selectors';
import { yourJourneySelectors } from '@store/yourJourney/selectors';
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { NextPage, NextPageContext } from 'next';
import { Store } from 'redux';
import { Property } from 'shared-types/Property';

type SpaDayPageProps = {
  id: string | undefined | string[];
  property: Property;
  language: string;
  socialFallbackImage: {
    url: string;
  };
  url: string;
  bookingStep: number | string | undefined | string[];
  checkIn: string;
};

const SpaDayPage: NextPage<SpaDayPageProps> = ({
  id,
  property,
  language,
  socialFallbackImage = {
    url: '',
  },
  url,
  bookingStep,
  checkIn,
}) => {
  const { data: spaDay, isFetching } = useGetSpaPackage(property.id, Number(id), language);

  useEffect(() => {
    spaDay &&
      analytics.track({
        event: 'view_item',
        params: {
          currency: spaDay.price.currencyCode,
          value: spaDay.price.amount,
          items: [
            {
              item_category: 'spa-packages',
              item_id: `${spaDay.id}`,
              item_name: spaDay.name,
              check_in: checkIn,
            },
          ],
        },
      });
  }, [spaDay]);

  if (!spaDay && !isFetching) {
    return <Error statusCode={404} />;
  }

  const pageTitle = property.propertyTitle
    ? `${spaDay?.name} | ${property.propertyTitle}`
    : spaDay?.name;

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      <NextSeo
        config={{
          openGraph: {
            title: pageTitle,
            description: spaDay?.description,
            locale: language,
            url,
            images: [{ url: spaDay?.image?.url || socialFallbackImage?.url }],
          },
        }}
      />
      <SpaDay
        id={Number.parseInt(id as string, 10)}
        key={id}
        property={property}
        language={language}
        bookingStep={bookingStep}
      />
    </>
  );
};

SpaDayPage.getInitialProps = async ({
  store,
  property,
  language,
  query,
  url,
  res,
}: NextPageContext & SpaDayPageProps & { store: Store }) => {
  const journeyParams = yourJourneySelectors.getParsedJourneyParams(store.getState());
  let spaDate = journeyParams.spaDate;

  const maxDaysAdvance = settingSelectors.getSpaDaysMaxDaysAdvance(store.getState());
  const maxDaysAdvanceDate = moment().add(maxDaysAdvance, 'days');
  const isOutsideAdvancePeriod = maxDaysAdvance && spaDate && spaDate.isAfter(maxDaysAdvanceDate);

  if (isOutsideAdvancePeriod) {
    await store.dispatch(yourJourneyActions.changeJourneyParam('spaDate', moment()));
    spaDate = yourJourneySelectors.getParsedJourneyParams(store.getState()).spaDate;
  }
  const isServer = !!res;
  const client = new QueryClient();
  await Promise.all([
    isServer && getSpaPackagePrefetch(client, property.id, Number(query.id), language),
  ]);

  return {
    id: query.id,
    property,
    language,
    url,
    socialFallbackImage: property.socialFallbackImage,
    bookingStep: query.bookingStep,
    checkIn: moment(journeyParams.spaDate).format('YYYY-MM-DD').toString(),
    dehydratedState: dehydrate(client),
  };
};

export default SpaDayPage as any;
